/**
 * Sessions Hook and Context for Chat History
 * Manages conversation sessions with database persistence (localStorage fallback)
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import * as api from '../lib/api'

const SessionsContext = createContext(null)

// Storage key for localStorage fallback
const STORAGE_KEY = 'trendy_sessions'

/**
 * Generate a unique session ID (for localStorage fallback)
 */
function generateId() {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Generate a title from the first message
 */
function generateTitle(content) {
  const cleaned = content.replace(/\n/g, ' ').trim()

  // Detect playbook prompts — extract a short, human-readable title
  const playbookPatterns = [
    { match: /^Run the (deposit invoice|shipping invoice|pro-forma invoice) workflow/i, title: (m) => m[1].replace(/\b\w/g, c => c.toUpperCase()) + ' Workflow' },
    { match: /^Run the (morning|daily|operations) (digest|briefing)/i, title: () => 'Morning Digest' },
    { match: /^Run my morning operations digest/i, title: () => 'Morning Digest' },
    { match: /^(Help me prepare for|Upcoming meetings)/i, title: () => 'Meeting Prep' },
    { match: /^(Check|Review|Analyze).{0,20}(credit|AR|AP|collections|cash|margin|inventory|vendor|quote|backlog|shipping)/i, title: (m) => {
      const topic = m[2].charAt(0).toUpperCase() + m[2].slice(1)
      const action = m[1]
      return `${action} ${topic}`
    }},
    { match: /^(Compare|Sync).{0,20}(HubSpot|CRM|ERP)/i, title: () => 'CRM ↔ ERP Sync' },
  ]

  for (const { match, title } of playbookPatterns) {
    const m = cleaned.match(match)
    if (m) return title(m)
  }

  // Strip markdown formatting for cleaner titles
  const noMarkdown = cleaned.replace(/\*\*/g, '').replace(/#{1,3}\s*/g, '').replace(/`[^`]*`/g, '')

  if (noMarkdown.length <= 50) return noMarkdown
  // Try to break at a word boundary
  const truncated = noMarkdown.substring(0, 50)
  const lastSpace = truncated.lastIndexOf(' ')
  return (lastSpace > 30 ? truncated.substring(0, lastSpace) : truncated.substring(0, 47)) + '...'
}

/**
 * Group sessions by date
 */
function groupSessionsByDate(sessions) {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)
  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
  const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

  const groups = {
    today: [],
    yesterday: [],
    lastWeek: [],
    lastMonth: [],
    older: [],
  }

  sessions.forEach(session => {
    const sessionDate = new Date(session.updatedAt || session.updated_at)

    if (sessionDate >= today) {
      groups.today.push(session)
    } else if (sessionDate >= yesterday) {
      groups.yesterday.push(session)
    } else if (sessionDate >= lastWeek) {
      groups.lastWeek.push(session)
    } else if (sessionDate >= lastMonth) {
      groups.lastMonth.push(session)
    } else {
      groups.older.push(session)
    }
  })

  return groups
}

/**
 * Normalize session object from API or localStorage
 */
function normalizeSession(session) {
  return {
    id: session.id,
    title: session.title || 'New conversation',
    messages: session.messages || [],
    createdAt: session.createdAt || session.created_at,
    updatedAt: session.updatedAt || session.updated_at,
    messageCount: session.message_count || session.messageCount || 0,
    lastMessage: session.last_message || session.lastMessage || null,
  }
}

/**
 * Sessions Provider Component
 */
export function SessionProvider({ children }) {
  const [sessions, setSessions] = useState([])
  const [currentSessionId, setCurrentSessionId] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [useDatabase, setUseDatabase] = useState(true)
  const savingRef = useRef(false)
  const renamedSessionsRef = useRef(new Set()) // Track sessions that have been auto-renamed

  // Load sessions on mount
  useEffect(() => {
    loadSessions()
  }, [])

  /**
   * Load sessions from database or localStorage
   */
  const loadSessions = async () => {
    try {
      // Try database first
      const response = await api.getSessions()
      if (response.sessions) {
        const normalized = response.sessions.map(normalizeSession)
        setSessions(normalized.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)))
        setUseDatabase(true)
        setIsLoaded(true)
        return
      }
    } catch (err) {
      console.warn('Database not available, falling back to localStorage:', err.message)
      setUseDatabase(false)
    }

    // Fallback to localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        setSessions(parsed.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)))
      }
    } catch (err) {
      console.error('Failed to load sessions from localStorage:', err)
    }
    setIsLoaded(true)
  }

  // Save sessions to localStorage when not using database
  useEffect(() => {
    if (isLoaded && !useDatabase && !savingRef.current) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions))
      } catch (err) {
        console.error('Failed to save sessions:', err)
      }
    }
  }, [sessions, isLoaded, useDatabase])

  /**
   * Create a new session
   */
  const createSession = useCallback(async () => {
    if (useDatabase) {
      try {
        const response = await api.createSession('New conversation')
        const newSession = normalizeSession(response)
        newSession.messages = []
        setSessions(prev => [newSession, ...prev])
        setCurrentSessionId(newSession.id)
        return newSession.id
      } catch (err) {
        console.error('Failed to create session in database:', err)
        // Fall through to localStorage
      }
    }

    // localStorage fallback
    const newSession = {
      id: generateId(),
      title: 'New conversation',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    setSessions(prev => [newSession, ...prev])
    setCurrentSessionId(newSession.id)

    return newSession.id
  }, [useDatabase])

  /**
   * Get the current session
   */
  const currentSession = sessions.find(s => s.id === currentSessionId) || null

  /**
   * Update a session's messages
   */
  const updateSessionMessages = useCallback((sessionId, messages) => {
    setSessions(prev => prev.map(session => {
      if (session.id !== sessionId) return session

      // Generate title from first user message if not set
      let title = session.title
      if (title === 'New conversation' && messages.length > 0) {
        const firstUserMsg = messages.find(m => m.role === 'user')
        if (firstUserMsg) {
          title = generateTitle(firstUserMsg.content)
        }
      }

      return {
        ...session,
        title,
        messages,
        updatedAt: new Date().toISOString(),
      }
    }))
  }, [])

  /**
   * Add a message to the current session
   * @param {Object} message - The message to add
   * @param {string} [targetSessionId] - Optional explicit session ID (used when session was just created)
   * @param {Object} [options]
   * @param {boolean} [options.skipServerPersist] - If true, skip the POST to
   *   /sessions/{id}/messages. Used by streaming assistant messages because
   *   backend/app/chat.py:_persisting_stream_wrapper now INSERTs them server-side
   *   at end-of-stream (audit Phase 1, 2026-05-23). Non-streaming fallback paths
   *   omit this flag so they still persist client-side.
   */
  const addMessage = useCallback(async (message, targetSessionId = null, options = {}) => {
    const sessionId = targetSessionId || currentSessionId
    if (!sessionId) {
      console.warn('addMessage called without a valid session ID')
      return
    }

    // Check if this session needs auto-naming (only for first user message)
    // Use a ref to track which sessions have already been renamed
    const shouldAutoName = message.role === 'user' && !renamedSessionsRef.current.has(sessionId)
    const generatedTitle = shouldAutoName ? generateTitle(message.content) : null

    // Update local state immediately for responsiveness
    setSessions(prev => prev.map(session => {
      if (session.id !== sessionId) return session

      const newMessages = [...session.messages, message]

      // Generate title from first user message if not set
      let title = session.title
      if (title === 'New conversation' && generatedTitle) {
        title = generatedTitle
      }

      return {
        ...session,
        title,
        messages: newMessages,
        updatedAt: new Date().toISOString(),
      }
    }))

    // Mark this session as renamed (before async operations)
    if (generatedTitle) {
      renamedSessionsRef.current.add(sessionId)
    }

    // Save to database in background — unless the caller told us the server
    // already persisted this message (streaming path).
    if (useDatabase && !options.skipServerPersist) {
      try {
        savingRef.current = true
        await api.addMessageApi(
          sessionId,
          message.role,
          message.content,
          message.toolsUsed || null,
          message.citations || null,
        )

        // Update title in database if this was the first user message
        if (generatedTitle) {
          console.log('Auto-naming session:', generatedTitle)
          await api.updateSession(sessionId, generatedTitle)
        }
      } catch (err) {
        console.error('Failed to save message to database:', err)
      } finally {
        savingRef.current = false
      }
    } else if (useDatabase && options.skipServerPersist && generatedTitle) {
      // Even when skipping message persist, we may still need to push the title
      // update (first-user-message naming).
      try {
        await api.updateSession(sessionId, generatedTitle)
      } catch (err) {
        console.error('Failed to update session title:', err)
      }
    }
  }, [currentSessionId, useDatabase])

  /**
   * Select a session
   */
  const selectSession = useCallback(async (sessionId) => {
    setCurrentSessionId(sessionId)

    // Load full session with messages from database
    if (useDatabase) {
      const session = sessions.find(s => s.id === sessionId)
      if (session && (!session.messages || session.messages.length === 0)) {
        try {
          const fullSession = await api.getSession(sessionId)
          const normalized = normalizeSession(fullSession)
          setSessions(prev => prev.map(s =>
            s.id === sessionId ? normalized : s
          ))
        } catch (err) {
          console.error('Failed to load session messages:', err)
        }
      }
    }
  }, [sessions, useDatabase])

  /**
   * Delete a session
   */
  const deleteSession = useCallback(async (sessionId) => {
    // Update local state immediately
    setSessions(prev => prev.filter(s => s.id !== sessionId))

    if (currentSessionId === sessionId) {
      setCurrentSessionId(null)
    }

    // Delete from database
    if (useDatabase) {
      try {
        await api.deleteSessionApi(sessionId)
      } catch (err) {
        console.error('Failed to delete session from database:', err)
      }
    }
  }, [currentSessionId, useDatabase])

  /**
   * Rename a session
   */
  const renameSession = useCallback(async (sessionId, newTitle) => {
    // Update local state immediately
    setSessions(prev => prev.map(session => {
      if (session.id !== sessionId) return session
      return {
        ...session,
        title: newTitle,
        updatedAt: new Date().toISOString(),
      }
    }))

    // Update in database
    if (useDatabase) {
      try {
        await api.updateSession(sessionId, newTitle)
      } catch (err) {
        console.error('Failed to rename session in database:', err)
      }
    }
  }, [useDatabase])

  /**
   * Get sessions grouped by date
   */
  const groupedSessions = groupSessionsByDate(sessions)

  /**
   * Search sessions
   */
  const searchSessions = useCallback((query) => {
    if (!query.trim()) return sessions

    const lowerQuery = query.toLowerCase()
    return sessions.filter(session => {
      // Search in title
      if (session.title.toLowerCase().includes(lowerQuery)) return true

      // Search in messages
      return session.messages.some(msg =>
        msg.content.toLowerCase().includes(lowerQuery)
      )
    })
  }, [sessions])

  const value = {
    sessions,
    currentSession,
    currentSessionId,
    groupedSessions,
    isLoaded,
    useDatabase,
    createSession,
    selectSession,
    deleteSession,
    renameSession,
    updateSessionMessages,
    addMessage,
    searchSessions,
  }

  return (
    <SessionsContext.Provider value={value}>
      {children}
    </SessionsContext.Provider>
  )
}

/**
 * Hook to access sessions context
 */
export function useSessions() {
  const context = useContext(SessionsContext)

  if (!context) {
    throw new Error('useSessions must be used within a SessionProvider')
  }

  return context
}

export default useSessions
